/**
 * CSS variable mapping
 *
 * Converts token reference paths to CSS custom-property names and
 * resolves StyleObjects to `var(--smth-...)` references.
 *
 * @module @smoothie-framework/ui-core/resolve
 */
import { createEmbeddedRefsGlobal, hasEmbeddedRefs, parseTokenReferenceContent, RE_STANDALONE_REF, } from './patterns';
// Category renames for CSS variable naming
const CATEGORY_RENAME = {
    borders: 'border',
    borderWidths: 'border-width',
    borderStyles: 'border-style',
    radii: 'radius',
};
/**
 * Convert camelCase to kebab-case.
 * Handles consecutive capitals: `'pointerEvents'` -> `'pointer-events'`.
 */
function toKebabCase(str) {
    return str
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
        .toLowerCase();
}
function sanitizeCssVarSegment(segment) {
    return toKebabCase(segment)
        .replace(/\//g, '-over-')
        .replace(/[^a-z0-9-]/g, '-');
}
/**
 * Convert a dotted token path to a CSS variable name.
 *
 * @example
 * tokenPathToCssVar('colors.blue.500')                // '--smth-colors-blue-500'
 * tokenPathToCssVar('button.primary.bg')              // '--smth-button-primary-background'
 * tokenPathToCssVar('typography.fontSize.sm')         // '--smth-font-size-sm'
 * tokenPathToCssVar('typography.lineHeight.moderate') // '--smth-line-height-moderate'
 * tokenPathToCssVar('typography.fontFamily.body')     // '--smth-font-body'
 * tokenPathToCssVar('radii.md')                        // '--smth-radius-md'
 */
export function tokenPathToCssVar(path, prefix = '--smth') {
    // Typography special-cases (flattened naming convention)
    if (path.startsWith('typography.')) {
        const rest = path.slice(11); // after 'typography.'
        if (rest.startsWith('fontSize.')) {
            return `${prefix}-font-size-${rest.slice(9)}`;
        }
        if (rest.startsWith('lineHeight.')) {
            return `${prefix}-line-height-${rest.slice(11)}`;
        }
        if (rest.startsWith('fontWeight.')) {
            return `${prefix}-font-weight-${rest.slice(11)}`;
        }
        if (rest.startsWith('letterSpacing.')) {
            return `${prefix}-letter-spacing-${rest.slice(14)}`;
        }
        if (rest.startsWith('fontFamily.')) {
            return `${prefix}-font-${rest.slice(11)}`;
        }
    }
    // General case: split on dots, rename categories, kebab-case last segment
    const segments = path.split('.');
    const mapped = segments.map((segment) => sanitizeCssVarSegment(CATEGORY_RENAME[segment] ?? segment));
    // Kebab-case the last segment (property name)
    if (mapped.length > 1) {
        const lastSegment = mapped[mapped.length - 1];
        if (lastSegment !== undefined) {
            mapped[mapped.length - 1] = sanitizeCssVarSegment(lastSegment);
        }
    }
    return `${prefix}-${mapped.join('-')}`;
}
/**
 * Convert a token reference string to a `var(--...)` CSS reference.
 *
 * Handles both standalone and embedded references.
 *
 * @example
 * toCssVarRef('{button.primary.bg}')
 * // 'var(--smth-button-primary-background)'
 *
 * toCssVarRef('1px solid {colors.neutral.300}')
 * // '1px solid var(--smth-colors-neutral-300)'
 */
export function toCssVarRef(ref, prefix = '--smth') {
    const convertReferenceContent = (content) => {
        const parsed = parseTokenReferenceContent(content);
        if (!parsed) {
            return undefined;
        }
        const tokenPath = `${parsed.category}.${parsed.path}`;
        const variableRef = `var(${tokenPathToCssVar(tokenPath, prefix)})`;
        if (parsed.opacity === undefined) {
            return variableRef;
        }
        return `color-mix(in srgb, ${variableRef} ${parsed.opacity}%, transparent)`;
    };
    // Standalone reference
    const match = RE_STANDALONE_REF.exec(ref);
    const standaloneReference = match?.[1];
    if (standaloneReference !== undefined) {
        return convertReferenceContent(standaloneReference) ?? ref;
    }
    // Embedded references
    if (hasEmbeddedRefs(ref)) {
        return ref.replace(createEmbeddedRefsGlobal(), (_, path) => convertReferenceContent(path) ?? `{${path}}`);
    }
    // Not a reference
    return ref;
}
/**
 * Resolve all string values in a flat StyleObject to CSS variable references.
 *
 * This is the **primary runtime path** for web components.
 * No token map is needed -- pure string rewriting.
 */
export function resolveStyleObjectToCssVars(styles, prefix = '--smth') {
    const result = {};
    for (const key in styles) {
        const val = styles[key];
        if (val === undefined)
            continue;
        result[key] = typeof val === 'string' ? toCssVarRef(val, prefix) : val;
    }
    return result;
}
//# sourceMappingURL=css-vars.js.map